<template>
  <div class="contact-list">
    <div class="d-flex justify-content-between align-items-center flex-wrap">
      <h6 v-if="!readonly && selectedIdsLength" class="mc-row-check mt-0">
        <span class="badge btn-success">{{ selectedIdsLength }}</span>
        {{ $t('global.object.selectedRow') }}
        <a class="color-primary" @click="handleUnselectAll">{{ $t('global.action.deselect') }}</a>
      </h6>

      <div class="d-flex justify-content-end flex-grow-1 gap-2 flex-wrap">
        <!-- Search Input -->
        <div id="tickets-table_filter" class="dataTables_filter text-md-end">
          <label
            class="d-inline-flex align-items-center mc-table-view__search"
            @click="focusSearch"
          >
            <i class="mc-fa fas fa-search"></i>
            <b-form-input
              v-model="filter"
              type="search"
              class="ms-2"
              ref="searchInput"
              :placeholder="$t('global.search') + '...'"
            />
          </label>
        </div>

        <!-- Delete Button -->
        <McButton
          v-if="!readonly && selectedIdsLength"
          variant="secondary-red"
          icon="icon-outline_trash"
          :text="$t('global.delete')"
          @click="handleOpenDeleteConfirm"
        />

        <!-- Add Button -->
        <McButton
          v-if="!readonly"
          icon="icon-fill_plus"
          :disabled="isLoading"
          :text="$t('global.action.create')"
          @click="handleOpenModalContactForm"
        />
      </div>
    </div>

    <div class="table-responsive my-3 border border-1">
      <b-table
        ref="table-ref"
        :class="['contact-table', { 'contact-table--selectable': !readonly }]"
        fixed
        show-empty
        select-mode="multi"
        :busy="isLoading"
        :items="items"
        :fields="computedFields"
        :per-page="limit"
        :currentPage="page"
        :filter="filter"
        :filter-included-fields="filterOn"
        @filtered="onFiltered"
      >
        <!-- Empty Result -->
        <template #empty>
          <div class="text-center my-4">
            {{ $t('trackingTemp.contact.noData') }}
          </div>
        </template>

        <!-- Slot: Header Checkbox -->
        <template #head(checkbox)="{ field }">
          <div class="form-check" :style="field?.thClass">
            <!-- <b-form-checkbox
              v-model="selectedAll"
              :disabled="items.length > 0"
              @change="handleSelectAll"
            /> -->
          </div>
        </template>

        <!-- Slot: Cell Checkbox -->
        <template #cell(checkbox)="{ item }">
          <div class="form-check">
            <b-form-checkbox
              :checked="selectedIds[item.contactId]"
              @change="(event) => handleSelect(event, item)"
            />
          </div>
        </template>

        <template #[`cell(location.fullName)`]="{ value, item }">
          <a v-if="value" class="__gmap-address" target="_blank" :href="getGoogleMapLink(item)">
            {{ value }}
          </a>
          <template v-else> - </template>
        </template>

        <template #cell(sequence)="{ index }">
          {{ (page - 1) * limit + index + 1 }}
        </template>

        <template #cell(email)="{ value }">
          {{ value }}
        </template>

        <template #cell(phoneNumber)="{ value }"> {{ value }} </template>

        <template #cell(createdOn)="{ value }">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
      </b-table>
    </div>

    <div class="d-flex flex-wrap flex-md-nowrap">
      <div class="mc-limit">
        <div class="__limit">
          <div>{{ $t('global.show') }}</div>
          <b-form-select
            v-model="limit"
            class="form-select form-select-sm __limit--dropdown"
            size="sm"
            :disabled="isLoading"
            :options="limitOptions"
          />
          <div>{{ $t('global.object.data').toLowerCase() }}.</div>
        </div>

        <div class="__info">
          <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
          {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
          .
        </div>
      </div>
      <div class="mc-pagination-container">
        <b-pagination
          v-model="page"
          :disabled="isLoading"
          :per-page="limit"
          :total-rows="totalCount || limit * page"
        />
      </div>
    </div>

    <McConfirmation
      v-model="isShowDeleteConfirm"
      centered
      noCloseOnEsc
      noCloseOnBackdrop
      title="Hapus Kontak Penerima"
      ok-title="Ya, Hapus"
      cancel-title="Batal"
      body-class="__modal-confirm-delete"
      @handle-ok="handleConfirmDelete"
      @cancel="handleCancelDeleteConfirm"
    >
      <div class="card-body mc-modal__filter">
        <p>Yakin hapus kontak berikut?</p>
        <b-table-simple outlined>
          <b-tbody>
            <b-tr v-for="item in selectedIdItems" :key="item.contactId">
              <b-td>{{ item.label || '-' }}</b-td>
              <b-td>{{ item.phoneNumber }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
        <p class="pt-3">
          Setelah klik Simpan, kontak tersebut tidak akan lagi menerima notifikasi pelacakan ini.
        </p>
      </div>
    </McConfirmation>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import McConfirmation from '@/components/McConfirmation'

import { formatIndexDate } from '@/helpers/tzDate'
import { contactFields } from '../lib/fields/contact-fields'
import { GOOGLE_MAP_ADDRESS_TYPE } from '../lib/constants/types'

import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McConfirmation,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isContractTempLv: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filter: null,
      filteredItems: [],
      limit: 10,
      page: 1,
      selectedIdsLength: 0,

      selectedIds: {},
      limitOptions: [10, 25, 50, 100],

      selectedAll: false,
      isShowDeleteConfirm: false,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),

    computedFields() {
      if (this.isContractTempLv) {
        return [
          {
            key: 'sequence',
            label: this.$t('trackingTemp.table.number'),
            thClass: 'mc-table--40',
          },
          {
            key: 'email',
            label: this.$t('trackingTemp.contact.email'),
          },
        ]
      }

      const fields = contactFields(this)

      if (this.isNotificationLocationFlagEnabled) {
        fields.splice(
          fields.findIndex((item) => item.key === 'location.fullName'),
          1
        )
      }

      if (this.readonly) {
        return fields
      }

      return [
        {
          key: 'checkbox',
          label: '',
          thClass: 'min-width: 13px',
        },
        ...fields,
      ]
    },

    filterOn() {
      if (this.isContractTempLv) {
        return ['email']
      }

      const fields = ['label', 'phoneNumber', 'createdBy']

      if (!this.isNotificationLocationFlagEnabled) {
        fields.push('location.fullName')
      }

      return fields
    },

    totalCount() {
      if (this.filter?.length > 0) {
        return this.filteredItems.length
      }

      return this.items.length
    },
    startData() {
      if (this.totalCount === 0) return 0

      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    selectedIdItems() {
      return this.items.filter((item) => this.selectedIds[item.contactId])
    },
    selectedItemFields() {
      return [
        {
          key: 'label',
          label: 'Nama',
        },
        {
          key: 'phoneNumber',
          label: 'No. Whatsapp',
        },
      ]
    },

    isNotificationLocationFlagEnabled() {
      return this.getFeatureFlagData('notification.temporary_track').value
    },
  },
  methods: {
    formatIndexDate,

    focusSearch() {
      this.$refs.searchInput.focus()
    },

    onFiltered(filteredItems) {
      this.filteredItems = filteredItems
      this.page = 1
    },

    getGoogleMapLink(item) {
      let link = ''

      if (item.location.type === GOOGLE_MAP_ADDRESS_TYPE && item.location.id) {
        link = `https://www.google.com/maps/place/?q=place_id:${item.location.id}`
      } else {
        link = `https://www.google.com/maps?q=${item.location.lat},${item.location.lng}`
      }

      return link
    },

    handleOpenModalContactForm() {
      this.$bvModal.show('modal-contact-form')
    },

    handleOpenDeleteConfirm() {
      this.isShowDeleteConfirm = true
    },

    handleCancelDeleteConfirm() {
      this.isShowDeleteConfirm = false
    },

    handleConfirmDelete() {
      let newItems = []

      if (this.selectedAll) {
        newItems = []
      } else {
        newItems = this.items.filter((item) => this.selectedIds[item.contactId] !== true)
      }

      this.$emit('update:items', newItems)

      this.selectedIdsLength = 0
      this.selectedIds = {}
      this.selectedAll = false
      this.isShowDeleteConfirm = false
    },

    handleSelect(checked, item) {
      this.selectedIds = { ...this.selectedIds, [item.contactId]: checked }
      if (checked) {
        this.selectedIdsLength += 1
      } else {
        this.selectedIdsLength -= 1
      }
    },

    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.items.map((key) => [key.contactId, true]))
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },

    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
  },
}
</script>

<style lang="scss" scoped>
.contact-list {
  .mc-limit {
    color: $mep-color-text;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    width: 100%;

    @media screen and (max-width: 468px) {
      justify-content: center;
    }

    .__limit {
      color: $mep-color-text;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;

      .__limit--dropdown {
        width: fit-content;
      }
    }

    .__info {
      flex-shrink: 0;
      color: $mep-color-text;

      strong {
        font-weight: 600;
      }
    }
  }

  :deep(.contact-table) {
    width: 100%;
    min-width: 600px;

    thead {
      tr {
        th {
          position: relative;
          .sort-icon-up {
            position: absolute;
            right: 10px;
            top: 8px;
            opacity: 0.7;
          }
          .sort-icon-down {
            position: absolute;
            right: 10px;
            bottom: 8px;
            opacity: 0.7;
          }
        }
      }
    }

    th,
    td {
      padding: 10px;
    }

    &.contact-table--selectable {
      td:nth-child(1),
      th:nth-child(1) {
        width: 0px;
      }
    }

    td.td__overflow-ellipsis {
      max-width: 150px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .__busy {
      min-height: min(50vh, 250px);
      display: grid;
      place-items: center;
      place-content: center;
    }

    .mc-datetime {
      color: $mep-color-text-200;
    }

    // Action column
    .mc-action-col-head {
      width: 80px;
    }

    // Action cell
    .mc-action-cell {
      vertical-align: middle;

      & > div {
        display: flex;
        padding: 0 4px;
        justify-content: space-between;
      }

      .mc-icon:before {
        font-size: 20px;
      }

      .btn {
        padding: 0;

        &.disabled {
          opacity: $btn-disabled-opacity;
          cursor: not-allowed;
          border: none;
        }
      }

      .btn:hover {
        border-color: transparent;
      }

      .__delete-button .mc-icon:before {
        color: $danger;
      }
    }
  }

  .__gmap-address {
    text-decoration: underline;
  }

  .mc-pagination-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;

    // remove default margin from pagination
    ul[role='menubar'] {
      margin-bottom: 0;
    }

    @media screen and (max-width: 468px) {
      justify-content: center;
    }

    :deep(.pagination) {
      --bs-pagination-border-width: 0;
      --bs-pagination-border-radius: 100%;

      .page-link {
        padding: 4px;
        min-height: 24px;
        aspect-ratio: 1 / 1;
        border-radius: 100%;
        font-size: 12px;
      }

      .page-link.active,
      .active > .page-link {
        background-color: $mep-primary-400;
      }

      .page-item {
        &:nth-child(1),
        &:nth-child(2),
        &:nth-last-child(1),
        &:nth-last-child(2) {
          .page-link {
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 16px;
            padding-bottom: 10%;
          }
        }
      }
    }
  }
}
</style>
